import React, { useEffect, useState } from 'react';
import { sortableContainer, sortableElement } from 'react-sortable-hoc';
import Collapse from '@material-ui/core/Collapse';

import PolicyRow from './PolicyRow';
import styles from './CssModules/PoliciesSection.module.css';
import commonStyles from './CssModules/CommonPolicyStyles.module.css';
import { POLICY_ACTION_TYPES } from '@silverfort-nodelibs/common/utils/consts';

const SortableItem = sortableElement(({ value }) => <div>{value}</div>);

const SortableContainer = sortableContainer(({ children }) => {
    return <span>{children}</span>;
});

const PoliciesSection = ({
    policiesData,
    changePolicyState,
    handleReorder,
    isEditor,
    isNewPolicies,
    handleDeletePolicy,
    handleSavePolicy,
    action: actionObj,
    policyConfiguration,
    setPolicyConfiguration,
    filteredData,
    onHeaderClicked,
    priorityOrder,
    setPriorityOrder,
    nextPolicyGroupId,
    setNextPolicyGroupId,
    duplicatePolicy,
    isCreateFromTemplate,
    onEditTemplate,
    templateToEdit = undefined
}) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [isPoliciesReordered, setIsPoliciesReordered] = useState(false);
    const [reorderErrorMsg, setReorderErrorMsg] = useState('');

    useEffect(() => {
        const onUnload = (event) => {
            if (isPoliciesReordered) {
                event.returnValue = 'You have some unsaved changes';
                return 'You have some unsaved changes';
            }
        };
        window.addEventListener('beforeunload', onUnload);
        return () => {
            window.removeEventListener('beforeunload', onUnload);
        };
    }, [isPoliciesReordered]);

    useEffect(() => {
        if (filteredData) {
            setIsPoliciesReordered(false);
        }
    }, [filteredData]);

    const onSortEnd = ({ oldIndex, newIndex }) => {
        if (oldIndex === newIndex) {
            return;
        }
        const newArray = [...priorityOrder];
        const movedObj = newArray.splice(oldIndex, 1)[0];
        newArray.splice(newIndex, 0, movedObj);
        setPriorityOrder(newArray);

        let lasOriginalPriority = -1;
        for (const priorityObj of newArray) {
            if (priorityObj.originalPriority <= lasOriginalPriority) {
                setIsPoliciesReordered(true);
                return;
            }
            lasOriginalPriority = priorityObj.originalPriority;
        }
        setIsPoliciesReordered(false);
    };

    const noPoliciesText = filteredData
        ? 'There are no policies. Revise your filter options.'
        : `There are no policies. Click 'Create policy' to add policies.`;
    let policiesRows = (
        <div className={`${commonStyles.basePolicyHeader} ${styles.noPoliciesRow}`}>{noPoliciesText}</div>
    );

    if (policiesData.length > 0) {
        if (actionObj.orderType === 'priority') {
            policiesRows = (
                <SortableContainer onSortEnd={onSortEnd} useDragHandle>
                    {policiesData.map((actionPolicy, index) => (
                        <SortableItem
                            key={`item-${actionPolicy.id}`}
                            index={index}
                            disabled={filteredData || !isEditor || actionPolicy.isExpanded}
                            value={
                                <PolicyRow
                                    key={`PolicyRow_${actionPolicy.id}`}
                                    policyData={actionPolicy}
                                    isEditor={isEditor}
                                    isSortable={true}
                                    changePolicyState={changePolicyState}
                                    isNewPolicies={isNewPolicies}
                                    handleDeletePolicy={handleDeletePolicy}
                                    handleSavePolicy={handleSavePolicy}
                                    policyConfiguration={policyConfiguration}
                                    setPolicyConfiguration={setPolicyConfiguration}
                                    filteredData={filteredData}
                                    onHeaderClicked={onHeaderClicked}
                                    nextPolicyGroupId={nextPolicyGroupId}
                                    setNextPolicyGroupId={setNextPolicyGroupId}
                                    duplicatePolicy={duplicatePolicy}
                                    isCreateFromTemplate={isCreateFromTemplate}
                                    templateToEdit={templateToEdit}
                                    onEditTemplate={onEditTemplate}
                                />
                            }
                        />
                    ))}
                </SortableContainer>
            );
        } else {
            policiesRows = policiesData.map((actionPolicy) => (
                <PolicyRow
                    key={`PolicyRow_${actionPolicy.id}`}
                    policyData={actionPolicy}
                    isEditor={isEditor}
                    changePolicyState={changePolicyState}
                    isNewPolicies={isNewPolicies}
                    handleDeletePolicy={handleDeletePolicy}
                    handleSavePolicy={handleSavePolicy}
                    policyConfiguration={policyConfiguration}
                    setPolicyConfiguration={setPolicyConfiguration}
                    onHeaderClicked={onHeaderClicked}
                    nextPolicyGroupId={nextPolicyGroupId}
                    setNextPolicyGroupId={setNextPolicyGroupId}
                    duplicatePolicy={duplicatePolicy}
                    isCreateFromTemplate={isCreateFromTemplate}
                    templateToEdit={templateToEdit}
                    onEditTemplate={onEditTemplate}
                />
            ));
        }
    }
    return (
        <div className={styles.policiesSection}>
            <div className={styles.policiesSectionTitle} onClick={() => setIsExpanded(!isExpanded)}>
                <div className={styles.mainTitle}>
                    {actionObj.title} {!isExpanded && <span>{` (${policiesData.length})`}</span>}
                </div>
                {actionObj.moreInfo && (
                    <div className={styles.moreInfoTitle}>
                        {actionObj.value === POLICY_ACTION_TYPES.MFA.value
                            ? actionObj.moreInfo.replace('Allow &', 'Allow,') + ' & Identity Bridge'
                            : actionObj.moreInfo}
                    </div>
                )}

                {isExpanded && <i className={`fa fa-angle-up ${styles.collapseIcon}`}></i>}
                {!isExpanded && <i className={`fa fa-angle-down ${styles.expandIcon}`}></i>}
            </div>
            <div className={styles.policiesSectionBody}>
                <Collapse in={isPoliciesReordered}>
                    <div className={`${commonStyles.basePolicyHeader} ${styles.approveReorderContainer}`}>
                        <button
                            className={commonStyles.policyBtn}
                            onClick={async () => {
                                const results = await handleReorder(true);
                                if (results) {
                                    setIsPoliciesReordered(false);
                                } else {
                                    setReorderErrorMsg('* Failed to update policies priority. Please try again.');
                                }
                            }}>
                            APPROVE REORDER
                        </button>
                        <div
                            className={styles.cancelReorderBtn}
                            onClick={() => {
                                handleReorder(false);
                                setIsPoliciesReordered(false);
                                setReorderErrorMsg('');
                            }}>
                            X CANCEL
                        </div>
                    </div>
                    {reorderErrorMsg && <span className={styles.reorderErrorMsg}>{reorderErrorMsg}</span>}
                </Collapse>
                <Collapse in={isExpanded}>{policiesRows}</Collapse>
            </div>
        </div>
    );
};

export default PoliciesSection;
